import {
  HttpClient,
  HttpHeaders,
  HttpParameterCodec,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { BootstrapAuthService } from '../../auth/services/bootstrap-auth.service';
import { CustomHttpUrlEncodingCodec } from './custom-http-parameter-codec';
import { ApiEndpoint } from './rest.model';

@Injectable()
export class RestService {
  public static readonly API_HTTP_REQUEST_TIMEOUT_MS: number = 10000;
  // tslint:disable-next-line
  private static readonly CUSTOM_PARAM_ENCODER: HttpParameterCodec = new CustomHttpUrlEncodingCodec();

  public get token(): any {
    return this.bootstrap.getToken() ? this.bootstrap.getToken() : undefined;
  }

  constructor(
    private readonly http: HttpClient,
    private readonly bootstrap: BootstrapAuthService,
  ) {}

  public getCall(
    api: ApiEndpoint,
    withAuth: boolean = true,
    routeParams?: any,
  ): Observable<any> {
    return this.http.get(this.buildEndPoint(api, routeParams), {
      withCredentials: true,
      headers: this.buildHeader(withAuth),
    });
  }

  public getCallWithParams(
    api: ApiEndpoint,
    withAuth: boolean = true,
    requestParams: HttpParams,
    routeParams?: any,
  ): Observable<any> {
    return this.http.get(this.buildEndPoint(api, routeParams), {
      withCredentials: true,
      headers: this.buildHeader(withAuth),
      params: this.buildParams(requestParams),
    });
  }

  public postCall(
    api: ApiEndpoint,
    payload: object,
    routeParams?: any,
  ): Observable<any> {
    return this.http.post(this.buildEndPoint(api, routeParams), payload, {
      headers: this.buildHeader(),
    });
  }

  public putCall(
    api: ApiEndpoint,
    payload: object,
    routeParams?: any,
  ): Observable<any> {
    return this.http.put(this.buildEndPoint(api, routeParams), payload, {
      headers: this.buildHeader(),
    });
  }

  public postCallWithParams(
    api: ApiEndpoint,
    payload: object,
    requestParams: HttpParams,
  ): Observable<any> {
    return this.http.post(this.buildEndPoint(api), payload, {
      headers: this.buildHeader(),
      params: this.buildParams(requestParams),
    });
  }

  public deleteCall(api: ApiEndpoint, routeParams?: any): Observable<any> {
    return this.http.delete(this.buildEndPoint(api, routeParams));
  }
  public deleteCallBody(api: ApiEndpoint, routeParams?: any, body?: any): Observable<any> {
    return this.http.delete(this.buildEndPoint(api, routeParams), {body: body});
  }

  /**
   * Build a string url endpoint from an ApiEndpoint enum and route parameters
   * Example: ```
   *   ApiEndpoint.TEST = 'somename/:someId/someOtherName/:someOtherId'
   *   const api = ApiEndpoint.TEST;
   *   const routeParams = { someId: '123', someOtherId: '456' };
   * ```
   *   A call to `buildEndPoint(api, routeParams)`,
   *   returns: 'somename/123/someOtherName/456'
   */
  public buildEndPoint(
    api: ApiEndpoint,
    routeParams?: any,
    appendToken: boolean = false,
  ): string {
    let url = `${environment.backendURL}/${api}`;
    if (routeParams) {
      Object.keys(routeParams).forEach(
        (param) => (url = url.replace(`:${param}`, encodeURIComponent(routeParams[param]))),
      );
    }
    return appendToken ? `${url}?token=${this.token}` : url;
  }

  private buildHeader(withAuth: boolean = true): HttpHeaders {
    let authHeader: HttpHeaders = new HttpHeaders();
    if (this.token && withAuth) {
      authHeader = authHeader.set('Authorization', `Bearer ${this.token}`);
    }
    return authHeader;
  }

  private buildParams(params: HttpParams): HttpParams {
    return new HttpParams({
      encoder: RestService.CUSTOM_PARAM_ENCODER,
      fromString: params.toString(),
    });
  }
}
